import { Button, Tag } from '@ads/components-react';
import {
  AcademicCapIcon,
  FlagIcon,
  HomeIcon,
  MapPinIcon,
  PencilSquareIcon,
  PhoneIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { CourseRegistrationManagementDetailsViewModel } from '@/shared/api/generated';

import { ENROLLMENT_CODE_TO_STATUS } from '../../../types/enrollmentStatusCode';
import { EnrollmentStatusBadge } from '../../atoms/EnrollmentStatusBadge';

function SectionCard({
  children,
  header,
}: {
  children: React.ReactNode;
  header?: React.ReactNode;
}) {
  return (
    <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface overflow-hidden">
      {header && (
        <div className="border-b-hairline border-ctx-layout-border px-xxs py-quark">{header}</div>
      )}
      <div className="px-xxs py-xxxs">{children}</div>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-nano">
      <div className="flex items-center gap-mini">
        {Icon && <Icon className="h-xxxs w-xxxs text-ctx-content-placeholder" />}
        <div className="flex flex-col">
          <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{title}</span>
          {description && (
            <span className="ts-caption-xxs text-ctx-content-placeholder">{description}</span>
          )}
        </div>
      </div>
      {action}
    </div>
  );
}

function InfoField({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-quark">
      <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
        {label}
      </span>
      <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{value ?? '—'}</span>
      {sub && <span className="ts-caption-xxs text-ctx-content-placeholder">{sub}</span>}
    </div>
  );
}

export interface EnrollmentDetailsTabProps {
  readonly details: CourseRegistrationManagementDetailsViewModel;
  readonly onEditPerson?: () => void;
}

/**
 * Aba de Detalhes da matrícula.
 * Exibe dados pessoais, contato, endereço, vínculo com o curso e flags.
 *
 * @param details - Dados vindos do endpoint /{courseRegistrationId}/details-tab
 * @param onEditPerson - Callback ao clicar em "Editar pessoa"
 */
export function EnrollmentDetailsTab({ details, onEditPerson }: EnrollmentDetailsTabProps) {
  const { t } = useTranslation('personManagement');

  const statusKey = details.enrollmentStatus
    ? ENROLLMENT_CODE_TO_STATUS[details.enrollmentStatus]
    : undefined;

  const pendingDocCount = (details.attachments ?? []).filter(
    g => g.documentationStatus === 0,
  ).length;
  const rejectedDocCount = (details.attachments ?? []).filter(
    g => g.documentationStatus === 2,
  ).length;

  return (
    <div className="flex flex-col gap-xxs bg-ctx-layout-body py-xs">
      {/* Dados pessoais */}
      <SectionCard
        header={
          <SectionTitle
            icon={UserCircleIcon}
            title={t('enrollmentDetail.personalData')}
            description={t('enrollmentDetail.personalDataDescription')}
            action={
              <Button
                hierarchy="tertiary"
                size="sm"
                leadingIcon={PencilSquareIcon}
                onClick={onEditPerson}
              >
                {t('enrollmentActions.editPerson')}
              </Button>
            }
          />
        }
      >
        <div className="grid grid-cols-2 gap-xxxs md:grid-cols-4">
          <InfoField
            label={t('enrollmentDetail.fullName').toUpperCase()}
            value={details.personFullName}
          />
          <InfoField
            label={t('enrollmentDetail.alias').toUpperCase()}
            value={details.personAlias}
          />
          <InfoField
            label={t('enrollmentDetail.email').toUpperCase()}
            value={details.personEmail}
          />
          <InfoField label={t('enrollmentDetail.cpf').toUpperCase()} value={details.cpf} />
          <InfoField label={t('enrollmentDetail.rg').toUpperCase()} value={details.rg} />
          <InfoField
            label={t('enrollmentDetail.passaporte').toUpperCase()}
            value={details.passport ?? (details.isForeign ? '—' : t('enrollmentDetail.notForeign'))}
          />
          <InfoField
            label={t('enrollmentDetail.language').toUpperCase()}
            value={details.communicationLanguage}
          />
        </div>
      </SectionCard>

      {/* Telefones + Endereço */}
      <div className="grid grid-cols-1 gap-xxxs md:grid-cols-2">
        <SectionCard
          header={<SectionTitle icon={PhoneIcon} title={t('enrollmentDetail.phones')} />}
        >
          <div className="grid grid-cols-2 gap-xxxs">
            <InfoField
              label={t('enrollmentDetail.phoneMobile').toUpperCase()}
              value={details.cellPhone}
            />
            <InfoField
              label={t('enrollmentDetail.phoneWork').toUpperCase()}
              value={details.workPhone}
            />
            <InfoField
              label={t('enrollmentDetail.phoneHome').toUpperCase()}
              value={details.homePhone}
            />
          </div>
        </SectionCard>

        <SectionCard
          header={<SectionTitle icon={HomeIcon} title={t('enrollmentDetail.address')} />}
        >
          {details.address ? (
            <p className="ts-paragraph-xs !font-medium text-ctx-content-title whitespace-pre-line">
              {details.address}
            </p>
          ) : (
            <div className="flex items-center gap-mini py-quark">
              <MapPinIcon className="h-xxxs w-xxxs shrink-0 text-ctx-content-placeholder" />
              <span className="ts-paragraph-xs text-ctx-content-placeholder">
                {t('enrollmentDetail.noAddress')}
              </span>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Vínculo com o curso */}
      <SectionCard
        header={<SectionTitle icon={AcademicCapIcon} title={t('enrollmentDetail.courseLink')} />}
      >
        <div className="grid grid-cols-2 gap-xxxs md:grid-cols-4">
          <InfoField
            label={t('enrollmentDetail.course').toUpperCase()}
            value={details.courseName}
          />
          <InfoField
            label={t('enrollmentDetail.turma').toUpperCase()}
            value={details.classStudentName}
          />
          <InfoField
            label={t('enrollmentDetail.polo').toUpperCase()}
            value={details.poloName ?? '—'}
            sub={!details.poloId ? t('enrollmentDetail.noPolo') : undefined}
          />
          <InfoField
            label={t('enrollmentDetail.enrolledSince').toUpperCase()}
            value={
              details.creationDate
                ? new Date(details.creationDate).toLocaleDateString('pt-BR')
                : undefined
            }
          />
          <div className="flex flex-col gap-quark">
            <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
              {t('enrollmentDetail.currentStatus').toUpperCase()}
            </span>
            {statusKey && <EnrollmentStatusBadge status={statusKey} />}
          </div>
          <div className="flex flex-col gap-quark">
            <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
              {t('enrollmentTabs.documents').toUpperCase()}
            </span>
            {pendingDocCount > 0 ? (
              <Tag
                label={t('enrollmentDetail.docPendingCount', {
                  count: pendingDocCount,
                })}
                size="sm"
                status="helper"
                custom={{ text: 'text-nowrap' }}
              />
            ) : (
              <Tag
                label={t('enrollmentDetail.docAllApproved')}
                size="sm"
                status="positive"
                custom={{ text: 'text-nowrap' }}
              />
            )}
            {rejectedDocCount > 0 && (
              <span className="ts-caption-xxs text-ctx-interactive-destructive">
                {t('enrollmentDetail.docRejectedCount', {
                  count: rejectedDocCount,
                })}
              </span>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Observação interna do aluno */}
      {details.studentNote && (
        <SectionCard
          header={<SectionTitle icon={FlagIcon} title={t('enrollmentDetail.studentNote')} />}
        >
          <p className="ts-paragraph-xs text-ctx-content-base whitespace-pre-line">
            {details.studentNote}
          </p>
        </SectionCard>
      )}

      {/* Flags simples */}
      <SectionCard
        header={<SectionTitle icon={FlagIcon} title={t('enrollmentDetail.flagsAndConditions')} />}
      >
        <div className="flex flex-wrap gap-quark">
          <Tag
            label={`${t('enrollmentDetail.foreignFlag')} · ${details.isForeign ? t('enrollmentDetail.flagYes') : t('enrollmentDetail.flagNo')}`}
            size="sm"
            status="default"
            custom={{ text: 'text-nowrap' }}
          />
          <Tag
            label={`${t('enrollmentDetail.pcdFlag')} · ${details.isPCD ? t('enrollmentDetail.flagYes') : t('enrollmentDetail.flagNo')}`}
            size="sm"
            status="default"
            custom={{ text: 'text-nowrap' }}
          />
          <Tag
            label={`${t('enrollmentDetail.scholarshipLinkFlag')} · ${details.enrollmentByScholarshipLink ? t('enrollmentDetail.flagYes') : t('enrollmentDetail.flagNo')}`}
            size="sm"
            status="default"
            custom={{ text: 'text-nowrap' }}
          />
        </div>
      </SectionCard>
    </div>
  );
}
